import React, { useCallback, useEffect, useRef, useState } from "react";
import LeftIcon from "../svg/LeftButton";
import RightIcon from "../svg/RightIcon";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Label,
} from "reactstrap";
import DateTimePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Breadcrumbs from "../../coreComponents/breadcrumbs";
import { format, startOfMonth, endOfMonth } from "date-fns";
import ApiService from "../../services/apiService";
import ExportExcel from "../reports/ExportExcel";
import { toast } from "react-toastify";

// ─── Month names ───────────────────────────────────────────
const MONTH_NAMES = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec",
];

// ─── Main Component ────────────────────────────────────────
const CombineReport = () => {
  const [fromDateTime, setFromDateTime] = useState<any>(() => startOfMonth(new Date()));
  const [toDateTime, setToDateTime] = useState<any>(() => new Date());

  // Monthly filter state
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [fromMonth, setFromMonth] = useState<number>(new Date().getMonth());
  const [toMonth, setToMonth] = useState<number>(new Date().getMonth());
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const [selectedReport, setSelectedReport] = useState<any>("DAILY");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [data, setData] = useState<any[]>([]);
  const [pumpingStationData, setPumpingStationData] = useState<any[]>([]);
  const [stationDropdownOpen, setStationDropdownOpen] = useState(false);
  const [selectedStationName, setSelectedStationName] = useState<any>(null);
  const [selectedStationId, setSelectedStationId] = useState<any>(null);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [fromMonthDropdownOpen, setFromMonthDropdownOpen] = useState(false);
  const [toMonthDropdownOpen, setToMonthDropdownOpen] = useState(false);
  const toggleStationDropdown = () => setStationDropdownOpen(!stationDropdownOpen);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  useEffect(() => {
    const getPumpingStation = async () => {
      try {
        const response = await ApiService.axiosApi(`/pumpingstation`, {
          method: "GET",
          params: { pageNumber: 0, pageSize: 0 },
        });
        if (response.success && response.data.data.length > 0) {
          setPumpingStationData(response.data.data);
          setSelectedStationName(response.data.data[0].name);
          setSelectedStationId(response.data.data[0].id);
        }
      } catch (e) {
        console.log(e);
      }
    };
    getPumpingStation();
  }, []);

  const handleReportChange = (report: string) => {
    setSelectedReport(report);
    if (report === "DAILY") {
      const now = new Date();
      const from = startOfMonth(now);
      const to = now;
      setFromDateTime(from);
      setToDateTime(to);
      fetchData(from, to, report);
    }
    if (report === "MONTHLY") {
      fetchMonthlyData(); // ← add this
    }
  };

  const SelectReport = [
    { id: 0, report: "DAILY" },
    { id: 3, report: "MONTHLY" },
  ];

  const validateGenerate = () => {
    if (!selectedStationName) { toast.warning("Please select Pumping Station"); return false; }
    if (!selectedReport) { toast.warning("Please select report type"); return false; }
    if (selectedReport === "MONTHLY") {
      if (fromMonth > toMonth) {
        toast.warning("From Month cannot be greater than To Month");
        return false;
      }
    } else {
      if (!fromDateTime) { toast.warning("Please select From Date"); return false; }
      if (!toDateTime) { toast.warning("Please select To Date"); return false; }
      if (new Date(fromDateTime) > new Date(toDateTime)) {
        toast.warning("From Date cannot be greater than To Date");
        return false;
      }
    }
    return true;
  };

  const fetchMonthlyData = async () => {
    const from = new Date(selectedYear, fromMonth, 1);
    const to = endOfMonth(new Date(selectedYear, toMonth, 1));
    try {
      const response = await ApiService.axiosApi(
        `/PumpingStation/getallpumprunhrsandkwh`,
        {
          method: "GET",
          params: {
            pageNumber,
            pageSize,
            fromDate: format(from, "yyyy-MM-dd HH:mm:ss"),
            toDate: format(to, "yyyy-MM-dd HH:mm:ss"),
            ReportType: "MONTHLY",
            Id: selectedStationId,
          },
        }
      );
      if (response.success) {
        setData(response.data.data ?? []);
        setTotalCount(response.data.totalCount ?? 0);
        if ((response.data.data ?? []).length === 0)
          toast.error("No data found for the selected months");
      } else {
        toast.error("Unable to get data at this moment");
      }
    } catch (e) {
      console.log(e);
      toast.error("Unable to get data at this moment");
    }
  };

  const fetchData = async (
    overrideFrom?: Date,
    overrideTo?: Date,
    overrideReport?: string
  ) => {
    const report = overrideReport ?? selectedReport;
    if (report === "MONTHLY") {
      await fetchMonthlyData();
      return;
    }
    const from = overrideFrom ?? fromDateTime;
    const to = overrideTo ?? toDateTime;
    try {
      const response = await ApiService.axiosApi(
        `/PumpingStation/getallpumprunhrsandkwh`,
        {
          method: "GET",
          params: {
            pageNumber,
            pageSize,
            fromDate: format(from, "yyyy-MM-dd HH:mm:ss"),
            toDate: format(to, "yyyy-MM-dd HH:mm:ss"),
            ReportType: "DAILY",
            Id: selectedStationId,
          },
        }
      );
      if (response.success) {
        setTotalCount(response.data.totalCount);
        setData(response.data.data);
      } else if (response.data?.data?.length === 0) {
        toast.error("No data found for the given date");
      } else {
        toast.error("Unable to get data at this moment");
      }
    } catch (e) {
      console.log(e);
    }
  };

  const getAllDataForExport = async () => {
    if (selectedReport === "MONTHLY") {
      const from = new Date(selectedYear, fromMonth, 1);
      const to = endOfMonth(new Date(selectedYear, toMonth, 1));
      try {
        const response = await ApiService.axiosApi(
          `/PumpingStation/getallpumprunhrsandkwh`,
          {
            method: "GET",
            params: {
              pageNumber: 0,
              pageSize: 0,
              fromDate: format(from, "yyyy-MM-dd HH:mm:ss"),
              toDate: format(to, "yyyy-MM-dd HH:mm:ss"),
              ReportType: "MONTHLY",
              Id: selectedStationId,
            },
          }
        );
        if (response.success) return response.data.data;
      } catch (e) {
        console.log(e);
      }
    } else {
      try {
        const response = await ApiService.axiosApi(
          `/PumpingStation/getallpumprunhrsandkwh`,
          {
            method: "GET",
            params: {
              pageNumber: 0,
              pageSize: 0,
              fromDate: fromDateTime ? format(fromDateTime, "yyyy-MM-dd HH:mm:ss") : "",
              toDate: toDateTime ? format(toDateTime, "yyyy-MM-dd HH:mm:ss") : "",
              ReportType: "DAILY",
              Id: selectedStationId,
            },
          }
        );
        if (response.success) return response.data.data;
      } catch (e) {
        console.log(e);
      }
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleNext = useCallback(() => {
    setPageNumber((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  const handlePrev = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  }, [totalPages]);

  useEffect(() => {
    if (selectedReport === "DAILY" && fromDateTime && toDateTime) fetchData();
    if (selectedReport === "MONTHLY") fetchMonthlyData();
  }, [pageNumber]);

  const formattedData = data?.map((row) => ({
    ...row,
    dateTime:
      selectedReport === "MONTHLY"
        ? format(new Date(row.dateTime), "MMM-yyyy")
        : format(new Date(row.dateTime), "dd-MM-yyyy"),
  }));

  const pumps = [
    { label: "Pump 1", runKey: "pmP1_RUNHRS", kwhKey: "pmP1_KWH" },
    { label: "Pump 2", runKey: "pmP2_RUNHRS", kwhKey: "pmP2_KWH" },
    { label: "Pump 3", runKey: "pmP3_RUNHRS", kwhKey: "pmP3_KWH" },
    { label: "Pump 4", runKey: "pmP4_RUNHRS", kwhKey: "pmP4_KWH" },
    { label: "Pump 5", runKey: "pmP5_RUNHRS", kwhKey: "pmP5_KWH" },
  ];

  const exportFrom =
    selectedReport === "MONTHLY"
      ? format(new Date(selectedYear, fromMonth, 1), "yyyy-MM-dd HH:mm:ss")
      : fromDateTime ? format(fromDateTime, "yyyy-MM-dd HH:mm:ss") : "";

  const exportTo =
    selectedReport === "MONTHLY"
      ? format(endOfMonth(new Date(selectedYear, toMonth, 1)), "yyyy-MM-dd HH:mm:ss")
      : toDateTime ? format(toDateTime, "yyyy-MM-dd HH:mm:ss") : "";

  return (
    <>
      <Breadcrumbs
        mainTitle="RunHours & Power Consumption Report"
        parent="Pumping Station Report"
      />
      <div className="w-100 bg-white px-2 py-2">
        {/* Filter Bar */}
        <div
          className="bg-light-primary d-flex justify-content-around align-items-center flex-wrap"
          style={{
            width: "100%",
            minHeight: "100px",
            border: "1px solid rgb(43,94,94,0.1)",
            padding: "8px 0",
          }}
        >
          {/* Pumping Station Dropdown */}
          <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label>Pumping Station:</Label>
            <Dropdown isOpen={stationDropdownOpen} toggle={toggleStationDropdown} className="custom-dropdown-wrapper">
              <DropdownToggle
                className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
                style={{ fontWeight: 500, borderRadius: "5px", padding: "8px 16px", width: "200px", height: "36px" }}
                color="whitesmoke"
              >
                {selectedStationName ?? "Pumping Station"}
              </DropdownToggle>
              <DropdownMenu
                className="custom-dropdown-menu"
                style={{ overflowY: "auto", boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)", borderRadius: "8px", padding: "8px 0" }}
              >
                {pumpingStationData.map((station: any) => (
                  <DropdownItem
                    key={station.id}
                    onClick={() => { setSelectedStationName(station.name); setSelectedStationId(station.id); }}
                  >
                    {station.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* Report Type Dropdown */}
          <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label >Report Type:</Label>
            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="custom-dropdown-wrapper">
              <DropdownToggle
                className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
                style={{ fontWeight: 500, borderRadius: "5px", padding: "8px 16px", width: "200px", height: "36px" }}
                color="whitesmoke"
              >
                {selectedReport ?? "Report"}
              </DropdownToggle>
              <DropdownMenu
                className="custom-dropdown-menu"
                style={{ overflowY: "auto", boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)", borderRadius: "8px", padding: "8px 0" }}
              >
                {SelectReport.map((item) => (
                  <DropdownItem key={item.id} onClick={() => handleReportChange(item.report)}>
                    {item.report}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* ── Date Pickers — conditional on report type ── */}
          {selectedReport === "MONTHLY" ? (
            <>
            {/* Year */}
<div className="report-input-div mx-4 input-border d-flex flex-column">
  <Label>Year:</Label>
  <Dropdown
    isOpen={yearDropdownOpen}
    toggle={() => setYearDropdownOpen(!yearDropdownOpen)}
    className="custom-dropdown-wrapper"
  >
    <DropdownToggle
      className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
      style={{ fontWeight: 500, borderRadius: "5px", padding: "8px 16px", width: "140px", height: "36px" }}
      color="whitesmoke"
    >
      {selectedYear}
    </DropdownToggle>
    <DropdownMenu className="custom-dropdown-menu" style={{ boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)", borderRadius: "8px", padding: "8px 0" }}>
      {yearOptions.map((y) => (
        <DropdownItem key={y} onClick={() => setSelectedYear(y)}>{y}</DropdownItem>
      ))}
    </DropdownMenu>
  </Dropdown>
</div>

{/* From Month */}
<div className="report-input-div mx-4 input-border d-flex flex-column">
  <Label>From Month:</Label>
  <Dropdown
    isOpen={fromMonthDropdownOpen}
    toggle={() => setFromMonthDropdownOpen(!fromMonthDropdownOpen)}
    className="custom-dropdown-wrapper"
  >
    <DropdownToggle
      className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
      style={{ fontWeight: 500, borderRadius: "5px", padding: "8px 16px", width: "140px", height: "36px" }}
      color="whitesmoke"
    >
      {MONTH_NAMES[fromMonth]}
    </DropdownToggle>
    <DropdownMenu className="custom-dropdown-menu" style={{ boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)", borderRadius: "8px", padding: "8px 0" }}>
      {MONTH_NAMES.map((name, idx) => (
        <DropdownItem key={idx} onClick={() => setFromMonth(idx)}>{name}</DropdownItem>
      ))}
    </DropdownMenu>
  </Dropdown>
</div>

{/* To Month */}
<div className="report-input-div mx-4 input-border d-flex flex-column">
  <Label>To Month:</Label>
  <Dropdown
    isOpen={toMonthDropdownOpen}
    toggle={() => setToMonthDropdownOpen(!toMonthDropdownOpen)}
    className="custom-dropdown-wrapper"
  >
    <DropdownToggle
      className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
      style={{ fontWeight: 500, borderRadius: "5px", padding: "8px 16px", width: "140px", height: "36px" }}
      color="whitesmoke"
    >
      {MONTH_NAMES[toMonth]}
    </DropdownToggle>
    <DropdownMenu className="custom-dropdown-menu" style={{ boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)", borderRadius: "8px", padding: "8px 0" }}>
      {MONTH_NAMES.map((name, idx) => (
        <DropdownItem key={idx} onClick={() => setToMonth(idx)}>{name}</DropdownItem>
      ))}
    </DropdownMenu>
  </Dropdown>
</div>
            </>
          ) : (
            <>
              <div className="report-input-div mx-4 input-border d-flex flex-column">
                <Label>From Date:</Label>
                <DateTimePicker
                  className="report-input"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD-MM-YYYY"
                  onChange={(date: any) => setFromDateTime(date)}
                  selected={fromDateTime}
                  isClearable
                />
              </div>
              <div className="report-input-div mx-4 input-border d-flex flex-column">
                <Label>To Date:</Label>
                <DateTimePicker
                  className="report-input"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD-MM-YYYY"
                  onChange={(date: any) => setToDateTime(date)}
                  selected={toDateTime}
                  isClearable
                />
              </div>
            </>
          )}

          {/* Generate */}
          <div className="d-flex flex-column" style={{ width: "8%" }}>
            <Label style={{ visibility: "hidden" }}>Generate</Label>
            <Button
              className="px-0"
              color="success"
              onClick={() => {
                if (!validateGenerate()) return;
                if (selectedReport === "MONTHLY") {
                  fetchMonthlyData();
                } else {
                  fetchData();
                }
              }}
              style={{ width: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              Generate
            </Button>
          </div>

          {/* Export */}
          <div style={{ width: "8%" }}>
            <Label style={{ visibility: "hidden" }}>Export</Label>
            <ExportExcel
              getData={getAllDataForExport}
              fileName="Pumping_Station_RunHours&kwh_Report"
              EffectiveFrom={exportFrom}
              toDate={exportTo}
              hiddenColumns={[]}
              columnOrder={["dateTime","pmP1_RUNHRS","pmP1_KWH","pmP2_RUNHRS","pmP2_KWH","pmP3_RUNHRS","pmP3_KWH","pmP4_RUNHRS","pmP4_KWH","pmP5_RUNHRS","pmP5_KWH","volume"]}
            />
          </div>
        </div>

        {/* Nested Header Table */}
        <div className="mt-2" style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
          <table
            className="table table-sm table-hover text-center"
            style={{ fontSize: "0.95rem", fontFamily: "Segoe UI, Roboto, sans-serif", width: "100%" }}
          >
            <thead className="bg-light-primary">
              <tr>
                <th
                  rowSpan={2}
                  style={{ fontWeight: 600, color: "#333", padding: "6px", fontSize: "1rem", height: "50px", verticalAlign: "middle", whiteSpace: "nowrap" }}
                >
                  {selectedReport === "MONTHLY" ? "Month" : "Date"}
                </th>
                {pumps.map((p) => (
                  <th
                    key={p.label}
                    colSpan={2}
                    style={{ fontWeight: 600, color: "#333", padding: "6px", fontSize: "1rem", textAlign: "center", borderBottom: "1px solid #dee2e6" }}
                  >
                    {p.label}
                  </th>
                ))}
                <th
                  rowSpan={2}
                  style={{ fontWeight: 600, color: "#333", padding: "6px", fontSize: "1rem", height: "50px", verticalAlign: "middle", whiteSpace: "nowrap" }}
                >
                  Volume (m³)
                </th>
              </tr>
              <tr>
                {pumps.map((p) => (
                  <React.Fragment key={p.label}>
                    <th style={{ fontWeight: 600, color: "#555", padding: "6px", fontSize: "0.85rem", whiteSpace: "nowrap" }}>Run Hrs</th>
                    <th style={{ fontWeight: 600, color: "#555", padding: "6px", fontSize: "0.85rem", whiteSpace: "nowrap" }}>KWH</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="table-body w-100">
              {formattedData && formattedData.length > 0 ? (
                formattedData.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: "5px" }}>{row.dateTime}</td>
                    {pumps.map((p) => (
                      <React.Fragment key={p.label}>
                        <td style={{ padding: "5px" }}>{row[p.runKey]}</td>
                        <td style={{ padding: "5px" }}>{row[p.kwhKey]}</td>
                      </React.Fragment>
                    ))}
                    <td style={{ padding: "5px" }}>{row.volume}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="text-center text-muted py-3">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-end align-content-center" style={{ marginRight: "10px", marginTop: "10px" }}>
          <p style={{ marginRight: "10px", marginTop: "8px" }}>
            Page {pageNumber} of {totalPages || 1}
          </p>
          <button onClick={handlePrev} disabled={pageNumber === 1} className={`icon-button ${pageNumber === 1 ? "disabled" : ""}`}>
            <LeftIcon className="icon" />
          </button>
          <button onClick={handleNext} disabled={pageNumber === totalPages || totalPages === 0} className={`icon-button ${pageNumber === totalPages ? "disabled" : ""}`}>
            <RightIcon className="icon" />
          </button>
        </div>
      </div>
    </>
  );
};

export default CombineReport;